import React, { useState, useContext, useMemo } from 'react';
import '../../GridEditor/BuildProgressionCalculator/EnhancedGoldControls.css';
import GoldDifferenceTimeline from '../../GridEditor/BuildProgressionCalculator/GoldDifferenceTimeline';
import GameAssetsContext from '../../../../XXX/context/GameAssetContext';

// Enhanced interfaces for detailed gold tracking (same as original)
export interface DetailedKill {
  id: string;
  value: number;
  wave: number;
  timing: string; // "2:15" format
  description?: string;
  xpValue?: number; // Calculated XP from this kill (based on victim level + comeback modifier)
}

export interface DetailedTurret {
  id: string;
  type: 'T1' | 'T2' | 'T3' | 'T4';
  position: 'Top' | 'Mid' | 'Bot' | 'Left' | 'Right';
  localGold: number;
  globalGold: number;
  wave: number;
  timing: string;
}

export interface DetailedPlate {
  id: string;
  turretType: 'T1' | 'T2' | 'T3';
  position: 'Top' | 'Mid' | 'Bot';
  count: number; // 1-15 plates per lane (5 per turret tier)
  goldValue: number; // count * 120
  wave: number;
  timing: string;
}

export interface DetailedMisc {
  id: string;
  value: number;
  wave: number;
  timing: string; // "2:15" format
  description?: string;
}

export interface CSPMRange {
  id: string;
  fromWave: number;       // inclusive
  toWave?: number;        // inclusive (undefined => no end)
  cspm: number;           // 4..15 etc.
  note?: string;
}

export interface GoldEventsData {
  cspm: number; // Base CSPM
  cspmRanges?: CSPMRange[];  // CSPM ranges
  kills: DetailedKill[];
  turrets: DetailedTurret[];
  plates: DetailedPlate[];
  miscellaneous: DetailedMisc[];
  /** Support item charge procs per minute (0–3, default 3). Controls Shared Riches gold generation speed. */
  supportProcsPerMin?: number;
}

// Types for turret definitions (same as original)
type TurretTier = { label: string; localGold: number; globalGold: number };
type TurretPosition = { label: string; tiers: Record<string, TurretTier> };

const TURRET_DEFINITIONS: Record<string, TurretPosition> = {
  Top: {
    label: 'Top',
    tiers: {
      T1: { label: 'Outer', localGold: 250, globalGold: 50 },
      T2: { label: 'Inner', localGold: 675, globalGold: 25 },
      T3: { label: 'Inhibitor', localGold: 375, globalGold: 25 }
    }
  },
  Mid: {
    label: 'Mid',
    tiers: {
      T1: { label: 'Outer', localGold: 250, globalGold: 50 },
      T2: { label: 'Inner', localGold: 425, globalGold: 25 }, // Mid is different
      T3: { label: 'Inhibitor', localGold: 375, globalGold: 25 }
    }
  },
  Bot: {
    label: 'Bot',
    tiers: {
      T1: { label: 'Outer', localGold: 250, globalGold: 50 },
      T2: { label: 'Inner', localGold: 675, globalGold: 25 },
      T3: { label: 'Inhibitor', localGold: 375, globalGold: 25 }
    }
  },
  Nexus: {
    label: 'Nexus',
    tiers: {
      T4: { label: 'Nexus', localGold: 0, globalGold: 50 }
    }
  }
};

const PLATE_POSITIONS = ['Top', 'Mid', 'Bot'] as const;

// Role Quest Types
export type Role = 'top' | 'jgl' | 'mid' | 'bot' | 'sup';

const ROLE_LABELS: Record<Role, string> = {
  top: 'Top',
  jgl: 'Jgl',
  mid: 'Mid',
  bot: 'Bot',
  sup: 'Sup'
};

// Role Quest Configuration (points required, passive generation, and rewards)
interface RoleQuestConfig {
  pointsRequired: number;
  lanePosition: string;       // 'Top' | 'Mid' | 'Bot' matching turret/plate position
  passiveRate: number;         // points per second in own lane (8 per 5s = 1.6)
  passiveStart: number;        // seconds when passive starts (65 = 1:05)
  pointsPerKill: number;
  pointsPerCS: number;         // in own lane
  pointsPerTurretOwn: number;
  pointsPerTurretOther: number;
  pointsPerPlateOwn: number;
  pointsPerPlateOther: number;
  rewards: {
    description: string;
    immediateGold?: number;
    bonusGoldPerMinion?: number;
    bonusGoldPerTakedown?: number;
    bonusXP?: number;
    bonusXPPercent?: number;
    levelCapIncrease?: number;
    bootsUpgradeValue?: number;
  };
}

const ROLE_QUEST_CONFIG: Partial<Record<Role, RoleQuestConfig>> = {
  top: {
    pointsRequired: 1200,
    lanePosition: 'Top',
    passiveRate: 1.6,
    passiveStart: 65,
    pointsPerKill: 15,
    pointsPerCS: 2,
    pointsPerTurretOwn: 50,
    pointsPerTurretOther: 25,
    pointsPerPlateOwn: 40,
    pointsPerPlateOther: 20,
    rewards: {
      description: 'Level cap → 20, +600 XP, +12.5% XP',
      bonusXP: 600,
      bonusXPPercent: 12.5,
      levelCapIncrease: 2
    }
  },
  mid: {
    pointsRequired: 1350,
    lanePosition: 'Mid',
    passiveRate: 1.6,
    passiveStart: 65,
    pointsPerKill: 25,
    pointsPerCS: 2,
    pointsPerTurretOwn: 50,
    pointsPerTurretOther: 25,
    pointsPerPlateOwn: 40,
    pointsPerPlateOther: 20,
    rewards: {
      description: 'Free T2 Boots upgrade (T1 → T2 in inventory)',
      bootsUpgradeValue: 350
    }
  },
  bot: {
    pointsRequired: 1350,
    lanePosition: 'Bot',
    passiveRate: 1.6,
    passiveStart: 65,
    pointsPerKill: 15,
    pointsPerCS: 3,
    pointsPerTurretOwn: 50,
    pointsPerTurretOther: 25,
    pointsPerPlateOwn: 40,
    pointsPerPlateOther: 20,
    rewards: {
      description: '+300g, +2g per minion, +50g per takedown',
      immediateGold: 300,
      bonusGoldPerMinion: 2,
      bonusGoldPerTakedown: 50
    }
  }
};

interface Props {
  goldEvents: GoldEventsData;
  onChange: (goldEvents: GoldEventsData) => void;
  primaryGoldEvents?: GoldEventsData;
  opponentGoldEvents?: GoldEventsData;
  maxWave?: number;
  className?: string;
  primaryChampionName?: string;
  opponentChampionName?: string;
  activeChampion?: 'primary' | 'opponent';
  onActiveChampionChange?: (champion: 'primary' | 'opponent') => void;
  maxWaveLimit?: number;
  onIncreaseWaveLimit?: () => void;
  
  // Wave progression data for the gold graph
  primaryWaveProgression?: any[];
  opponentWaveProgression?: any[];
  
  // Role quest
  primaryRole?: Role;
  opponentRole?: Role;
  onPrimaryRoleChange?: (role: Role) => void;
  onOpponentRoleChange?: (role: Role) => void;
  onQuestCompletionChange?: (primaryWave: number | null, opponentWave: number | null) => void;
}

const GoldControlsV2: React.FC<Props> = ({
  goldEvents,
  onChange,
  primaryGoldEvents,
  opponentGoldEvents,
  maxWave = 100,
  className = '',
  primaryChampionName = 'Primary',
  opponentChampionName = 'Opponent',
  activeChampion = 'primary',
  onActiveChampionChange,
  maxWaveLimit,
  onIncreaseWaveLimit,
  primaryWaveProgression = [],
  opponentWaveProgression = [],
  primaryRole: externalPrimaryRole,
  opponentRole: externalOpponentRole,
  onPrimaryRoleChange,
  onOpponentRoleChange,
  onQuestCompletionChange
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  
  // Local role state (used if external state not provided)
  const [localPrimaryRole, setLocalPrimaryRole] = useState<Role>('mid');
  const [localOpponentRole, setLocalOpponentRole] = useState<Role>('mid');
  
  // Use external or local role state
  const primaryRole = externalPrimaryRole ?? localPrimaryRole;
  const opponentRole = externalOpponentRole ?? localOpponentRole;
  
  const handlePrimaryRoleChange = (role: Role) => {
    if (onPrimaryRoleChange) {
      onPrimaryRoleChange(role);
    } else {
      setLocalPrimaryRole(role);
    }
  };
  
  const handleOpponentRoleChange = (role: Role) => {
    if (onOpponentRoleChange) {
      onOpponentRoleChange(role);
    } else {
      setLocalOpponentRole(role);
    }
  };
  
  // Get current role based on active champion
  const activeRole = activeChampion === 'primary' ? primaryRole : opponentRole;
  const handleActiveRoleChange = activeChampion === 'primary' ? handlePrimaryRoleChange : handleOpponentRoleChange;
  

  // ---------- Wave timing helpers (matches backend WaveGoldCalculator Season 2025) ----------
  // Wave intervals change at different game times:
  //   Phase 1 (waves 1-28):  30s intervals, first spawn at 0:30
  //   Phase 2 (waves 29-66): 25s intervals, from 14:00
  //   Phase 3 (waves 67+):   20s intervals, from 29:50
  const FIRST_WAVE_SPAWN = 30;   // 0:30
  const WAVE_TRAVEL_TIME = 30;   // 30s to reach lane
  const WAVE_CLEAR_TIME = 30;    // 30s to clear
  const WAVE_INTERVAL_EARLY = 30; // waves 1-28
  const WAVE_INTERVAL_MID = 25;   // waves 29-66
  const WAVE_INTERVAL_LATE = 20;  // waves 67+
  const WAVE_AT_14_MIN = 28;
  const WAVE_AT_30_MIN = 66;

  function getWaveSpawnTime(waveNumber: number): number {
    if (waveNumber <= 0) return 0;
    if (waveNumber <= WAVE_AT_14_MIN) {
      return FIRST_WAVE_SPAWN + (waveNumber - 1) * WAVE_INTERVAL_EARLY;
    } else if (waveNumber <= WAVE_AT_30_MIN) {
      const baseTime = 840; // 14:00 in seconds
      return baseTime + (waveNumber - WAVE_AT_14_MIN) * WAVE_INTERVAL_MID;
    } else {
      const baseTime = 1790; // 29:50 in seconds
      return baseTime + (waveNumber - WAVE_AT_30_MIN) * WAVE_INTERVAL_LATE;
    }
  }

  function getWaveArrivalTime(waveNumber: number): number {
    return getWaveSpawnTime(waveNumber) + WAVE_TRAVEL_TIME;
  }

  function getWaveClearTime(waveNumber: number): number {
    return getWaveArrivalTime(waveNumber) + WAVE_CLEAR_TIME;
  }

  function waveToTiming(wave: number): string {
    const clearSeconds = getWaveClearTime(wave);
    const minutes = Math.floor(clearSeconds / 60);
    const seconds = Math.floor(clearSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  
  // Season 2025: Plates no longer have a time limit - they exist until the turret is destroyed
  function arePlatesAvailable(_wave: number): boolean {
    return true; // Plates are always available now
  }
  
  function getLastPlateWave(): number {
    return maxWave; // No time limit for plates anymore
  }
  
  function timingToWave(timing: string, effectiveMaxWave?: number): number {
    const [m, s] = timing.split(':').map(Number);
    const totalSeconds = (m || 0) * 60 + (s || 0);
    const maxWaveToUse = effectiveMaxWave || maxWaveLimit || maxWave;
    for (let wave = 1; wave <= maxWaveToUse; wave++) {
      if (totalSeconds <= getWaveClearTime(wave)) return wave;
    }
    return maxWaveToUse;
  }

  // ---------- XP helpers (matches backend WaveGoldCalculator) ----------
  // Solo kill XP by slain champion level (index = level)
  const SOLO_KILL_XP = [
    0,    // level 0 (unused)
    42,   // level 1
    114,  // level 2
    144,  // level 3
    174,  // level 4
    204,  // level 5
    234,  // level 6
    308,  // level 7
    392,  // level 8
    486,  // level 9
    590,  // level 10
    640,  // level 11
    690,  // level 12
    740,  // level 13
    790,  // level 14
    840,  // level 15
    890,  // level 16
    940,  // level 17
    990,  // level 18
    1040, // level 19 (assumed +50)
    1090, // level 20 (assumed +50)
  ];

  // Shared kill XP TOTAL by slain champion level (index = level)
  // When 2+ allies earn the bounty, total pool is a % of solo XP:
  //   Levels 1-6:  66.6% of solo
  //   Levels 7-8:  82%   of solo
  //   Levels 9+:   90%   of solo
  // Each ally gets total / numAllies (e.g. duo → total / 2).
  const SHARED_KILL_XP_TOTAL = [
    0,    // level 0 (unused)
    28,   // level 1  (42 × 0.666)
    76,   // level 2  (114 × 0.666)
    96,   // level 3  (144 × 0.666)
    116,  // level 4  (174 × 0.666)
    136,  // level 5  (204 × 0.666)
    156,  // level 6  (234 × 0.666)
    253,  // level 7  (308 × 0.82)
    321,  // level 8  (392 × 0.82)
    437,  // level 9  (486 × 0.90)
    531,  // level 10 (590 × 0.90)
    576,  // level 11 (640 × 0.90)
    621,  // level 12 (690 × 0.90)
    666,  // level 13 (740 × 0.90)
    711,  // level 14 (790 × 0.90)
    756,  // level 15 (840 × 0.90)
    801,  // level 16 (890 × 0.90)
    846,  // level 17 (940 × 0.90)
    891,  // level 18 (990 × 0.90)
    936,  // level 19 (1040 × 0.90)
    981,  // level 20 (1090 × 0.90)
  ];

  // Cumulative XP required for each level (index = level)
  const LEVEL_XP_TABLE = [
    0,      // level 0
    0,      // level 1
    280,    // level 2
    660,    // level 3
    1140,   // level 4
    1720,   // level 5
    2400,   // level 6
    3180,   // level 7
    4060,   // level 8
    5040,   // level 9
    6120,   // level 10
    7300,   // level 11
    8580,   // level 12
    9960,   // level 13
    11440,  // level 14
    13020,  // level 15
    14700,  // level 16
    16480,  // level 17
    18360,  // level 18
    20340,  // level 19
    22420,  // level 20
  ];

  // Base minion XP values
  const BASE_XP = { melee: 65, caster: 32, cannon: 100 };
  const XP_SHARING: Record<string, number> = { solo: 0.95, duo: 0.62 };

  function getLaneType(role?: Role): 'solo' | 'duo' {
    return (role === 'bot' || role === 'sup') ? 'duo' : 'solo';
  }

  function getXPMultiplier(role?: Role): number {
    return XP_SHARING[getLaneType(role)] ?? 0.95;
  }

  function getLevelFromXP(cumulativeXP: number, maxLevel: number): number {
    for (let lvl = Math.min(maxLevel, 20); lvl >= 1; lvl--) {
      if (cumulativeXP >= LEVEL_XP_TABLE[lvl]) return lvl;
    }
    return 1;
  }

  /**
   * Comeback bonus/penalty modifier for kill XP.
   * |diff| ≤ 1 → 0%, then 20% per level diff beyond 1.
   * Penalty (killer higher) capped at −60%. Bonus (killer lower) uncapped.
   */
  function getComebackModifier(killerLevel: number, victimLevel: number): number {
    const diff = Math.abs(killerLevel - victimLevel);
    if (diff <= 1) return 0;
    const magnitude = (diff - 1) * 0.20;
    if (killerLevel > victimLevel) return -Math.min(magnitude, 0.60); // penalty
    return magnitude; // bonus
  }

  function getSoloKillXP(victimLevel: number): number {
    const idx = Math.max(1, Math.min(victimLevel, SOLO_KILL_XP.length - 1));
    return SOLO_KILL_XP[idx];
  }

  /** Kill XP per person: solo lanes get full solo XP, duo lanes (bot/sup) get shared total / 2. */
  function getKillXP(victimLevel: number, role?: Role): number {
    if (role === 'bot' || role === 'sup') {
      const idx = Math.max(1, Math.min(victimLevel, SHARED_KILL_XP_TOTAL.length - 1));
      return Math.round(SHARED_KILL_XP_TOTAL[idx] / 2);
    }
    return getSoloKillXP(victimLevel);
  }

  /** Get minion composition for a wave (matches backend getWaveComposition). */
  function getWaveComposition(waveNumber: number): { melee: number; caster: number; cannon: number } {
    const spawnTime = getWaveSpawnTime(waveNumber);
    // Wave type
    let hasCannon = false;
    if (waveNumber >= 3) {
      if (waveNumber <= WAVE_AT_14_MIN) {
        hasCannon = waveNumber % 3 === 0;
      } else if (waveNumber >= 55) { // WAVE_AT_25_MIN
        hasCannon = true;
      } else {
        hasCannon = waveNumber % 2 === 1;
      }
    }
    let melee = 3, caster = 3, cannon = hasCannon ? 1 : 0;
    if (spawnTime >= 840 && hasCannon) melee = 2;
    if (spawnTime >= 1800) caster = 2;
    return { melee, caster, cannon };
  }

  /** Calculate minion XP for a single wave. */
  function getWaveMinionXP(waveNumber: number, xpMult: number): number {
    const comp = getWaveComposition(waveNumber);
    return Math.round(
      (BASE_XP.melee * comp.melee + BASE_XP.caster * comp.caster + BASE_XP.cannon * comp.cannon) * xpMult
    );
  }

  /**
   * Estimate kill XP for a kill at a given wave.
   * Assumption: equal-level kills (victim level = killer level, no comeback modifier).
   * Duo lanes (bot/sup) use shared kill XP (total / 2).
   */
  function estimateKillXP(killWave: number, role?: Role): number {
    const xpMult = getXPMultiplier(role);
    const maxLevel = role === 'top' ? 20 : 18;

    // Calculate killer's level from minion XP up to this wave
    let killerXP = 0;
    for (let w = 1; w <= killWave; w++) killerXP += getWaveMinionXP(w, xpMult);
    const killerLevel = getLevelFromXP(killerXP, maxLevel);

    // Equal-level assumption: victim = killer, no comeback modifier
    return getKillXP(killerLevel, role);
  }

  // ── Quest completion calculator (local, responsive to event changes) ──
  // Calculates the wave at which the quest completes, both baseline (passive + CS only)
  // and with events (kills, turrets, plates). Uses same logic as backend.
  function calcQuestCompletion(role: Role | undefined, events: GoldEventsData | undefined) {
    const cfg = role ? ROLE_QUEST_CONFIG[role] : undefined;
    if (!cfg) return null;

    const baseCspm = events?.cspm || goldEvents.cspm;
    const ranges = events?.cspmRanges || goldEvents.cspmRanges;
    const kills = [...(events?.kills || [])].sort((a, b) => a.wave - b.wave);
    const turrets = [...(events?.turrets || [])].sort((a, b) => a.wave - b.wave);
    const plates = [...(events?.plates || [])].sort((a, b) => a.wave - b.wave);

    let baselineWave: number | null = null;
    let withEventsWave: number | null = null;
    let kIdx = 0, tIdx = 0, pIdx = 0;
    let eventKillQP = 0, eventTurretQP = 0, eventPlateQP = 0;

    const MAX_SCAN = 120;

    for (let w = 1; w <= MAX_SCAN; w++) {
      const clearSec = getWaveSpawnTime(w) + WAVE_TRAVEL_TIME + WAVE_CLEAR_TIME;
      const gameMin = clearSec / 60;

      const passivePts = Math.max(0, clearSec - cfg.passiveStart) * cfg.passiveRate;
      // Use buildTimeline to account for CSPM ranges
      const tl = buildTimeline(baseCspm, ranges, gameMin);
      let totalCS = 0;
      for (const seg of tl) {
        const segStart = Math.max(seg.start, 1.5); // CS starts at 1:30
        const segEnd = seg.end;
        if (segEnd > segStart) totalCS += seg.cspm * (segEnd - segStart);
      }
      const csPts = totalCS * cfg.pointsPerCS;
      const baseTotal = passivePts + csPts;

      if (baselineWave === null && baseTotal >= cfg.pointsRequired) baselineWave = w;

      while (kIdx < kills.length && kills[kIdx].wave <= w) { eventKillQP += cfg.pointsPerKill; kIdx++; }
      while (tIdx < turrets.length && turrets[tIdx].wave <= w) {
        const isOwn = turrets[tIdx].position === cfg.lanePosition;
        eventTurretQP += isOwn ? cfg.pointsPerTurretOwn : cfg.pointsPerTurretOther;
        tIdx++;
      }
      while (pIdx < plates.length && plates[pIdx].wave <= w) {
        const isOwn = plates[pIdx].position === cfg.lanePosition;
        eventPlateQP += (isOwn ? cfg.pointsPerPlateOwn : cfg.pointsPerPlateOther) * plates[pIdx].count;
        pIdx++;
      }

      const withEventsTotal = baseTotal + eventKillQP + eventTurretQP + eventPlateQP;
      if (withEventsWave === null && withEventsTotal >= cfg.pointsRequired) withEventsWave = w;

      if (baselineWave !== null && withEventsWave !== null) break;
    }

    const totalEventQP = eventKillQP + eventTurretQP + eventPlateQP;
    const wavesSaved = (baselineWave && withEventsWave) ? baselineWave - withEventsWave : 0;

    return {
      baselineWave,
      baselineTime: baselineWave ? waveToTiming(baselineWave) : null,
      withEventsWave: totalEventQP > 0 ? withEventsWave : null,
      withEventsTime: (totalEventQP > 0 && withEventsWave) ? waveToTiming(withEventsWave) : null,
      wavesSaved,
      totalEventQP,
      eventKillQP,
      eventTurretQP,
      eventPlateQP,
      killCount: kills.length,
      turretCount: turrets.length,
      plateCount: plates.reduce((s, p) => s + p.count, 0),
    };
  }

  const primaryQuestCompletion = useMemo(
    () => calcQuestCompletion(primaryRole, primaryGoldEvents),
    [primaryRole, primaryGoldEvents, goldEvents.cspm, goldEvents.cspmRanges]
  );
  const opponentQuestCompletion = useMemo(
    () => calcQuestCompletion(opponentRole, opponentGoldEvents),
    [opponentRole, opponentGoldEvents, goldEvents.cspm, goldEvents.cspmRanges]
  );
  // Active champion's quest completion (for the quest section display)
  const questCompletion = activeChampion === 'primary' ? primaryQuestCompletion : opponentQuestCompletion;

  // Report quest completion waves to parent
  React.useEffect(() => {
    if (onQuestCompletionChange) {
      const pWave = primaryQuestCompletion?.withEventsWave ?? primaryQuestCompletion?.baselineWave ?? null;
      const oWave = opponentQuestCompletion?.withEventsWave ?? opponentQuestCompletion?.baselineWave ?? null;
      onQuestCompletionChange(pWave, oWave);
    }
  }, [primaryQuestCompletion, opponentQuestCompletion, onQuestCompletionChange]);

  // ---------- CSPM helpers (simplified - no wave math) ----------
  function normalizeCSPMRanges(ranges: CSPMRange[] = []): CSPMRange[] {
    const cleaned = ranges
      .map(r => ({
        ...r,
        fromWave: Math.max(1, Math.floor(r.fromWave)),
        toWave: r.toWave ? Math.max(r.fromWave, Math.floor(r.toWave)) : undefined
      }))
      .sort((a, b) => a.fromWave - b.fromWave || (a.toWave ?? 1e9) - (b.toWave ?? 1e9));

    const merged: CSPMRange[] = [];
    for (const r of cleaned) {
      const last = merged[merged.length - 1];
      const touches =
        last &&
        last.cspm === r.cspm &&
        (last.toWave === r.fromWave || ((last.toWave ?? r.fromWave) + 1 === r.fromWave));
      if (touches) {
        last.toWave = r.toWave ?? last.toWave;
      } else {
        merged.push({ ...r });
      }
    }
    return merged;
  }

  function tightenRanges(ranges: CSPMRange[] = []): CSPMRange[] {
    const norm = normalizeCSPMRanges(ranges);
    if (norm.length <= 1) return norm;

    const sorted = [...norm].sort((a, b) => a.fromWave - b.fromWave);
    for (let i = 0; i < sorted.length - 1; i++) {
      const cur = sorted[i];
      const next = sorted[i + 1];
      if (cur.toWave === undefined || cur.toWave > next.fromWave) {
        cur.toWave = next.fromWave;
      }
      if (cur.toWave < cur.fromWave) {
        cur.toWave = cur.fromWave;
      }
    }

    return normalizeCSPMRanges(sorted);
  }

  // Expand/collapse state
  const [expandedSections, setExpandedSections] = 
    useState<Set<'cspm' | 'kills' | 'turrets' | 'plates' | 'misc' | 'quest' | 'supportItem'>>(new Set());
  const toggleSection = (section: 'cspm' | 'kills' | 'turrets' | 'plates' | 'misc' | 'quest' | 'supportItem') => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(section) ? next.delete(section) : next.add(section);
      return next;
    });
  };

  // Timeline is always visible since we removed the header
  const timelineVisible = true;

  // Collapse all sections when switching champions
  React.useEffect(() => {
    // Clear all expanded sections when switching champions
    setExpandedSections(new Set());
  }, [activeChampion]);

  // Auto-collapse empty sections when data changes
  React.useEffect(() => {
    const newExpandedSections = new Set(expandedSections);
    
    // Collapse sections that have no data
    if (!goldEvents.cspmRanges || goldEvents.cspmRanges.length === 0) {
      newExpandedSections.delete('cspm');
    }
    if (goldEvents.kills.length === 0) {
      newExpandedSections.delete('kills');
    }
    if (goldEvents.turrets.length === 0) {
      newExpandedSections.delete('turrets');
    }
    if (goldEvents.plates.length === 0) {
      newExpandedSections.delete('plates');
    }
    if (goldEvents.miscellaneous.length === 0) {
      newExpandedSections.delete('misc');
    }
    
    // Only update if there are actual changes to avoid infinite loops
    if (newExpandedSections.size !== expandedSections.size || 
        [...newExpandedSections].some(section => !expandedSections.has(section))) {
      setExpandedSections(newExpandedSections);
    }
  }, [goldEvents.cspmRanges, goldEvents.kills, goldEvents.turrets, goldEvents.plates, goldEvents.miscellaneous]);

  // Quick-add CSPM range
  const [cspmQuick, setCspmQuick] = useState<{ value: number; from: number; to?: number }>(() => ({
    value: goldEvents.cspm ?? 10,
    from: 1,
    to: maxWaveLimit || maxWave
  }));

  // Auto-suggest next default after ranges change
  React.useEffect(() => {
    const tight = tightenRanges(goldEvents.cspmRanges || []);
    const last = tight[tight.length - 1];
    const effectiveMaxWave = maxWaveLimit || maxWave;
    if (last && typeof last.toWave === 'number') {
      const nextFrom = Math.min(last.toWave + 1, effectiveMaxWave);
      setCspmQuick(q => ({ ...q, from: nextFrom, to: effectiveMaxWave }));
    } else if (!tight.length) {
      setCspmQuick(q => ({ ...q, from: 1, to: effectiveMaxWave }));
    }
  }, [goldEvents.cspmRanges, maxWave, maxWaveLimit]);

  function handleAddCSPMRange() {
    const effectiveMaxWave = maxWaveLimit || maxWave;
    const newRange: CSPMRange = {
      id: `cspm-range-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      fromWave: Math.max(1, Math.min(effectiveMaxWave, Math.floor(cspmQuick.from))),
      toWave: cspmQuick.to
        ? Math.max(cspmQuick.from, Math.min(effectiveMaxWave, Math.floor(cspmQuick.to)))
        : effectiveMaxWave,
      cspm: Math.max(1, Math.min(50, cspmQuick.value))
    };

    const afterInsert = tightenRanges([...(goldEvents.cspmRanges || []), newRange]);
    onChange({ ...goldEvents, cspmRanges: afterInsert });

    setExpandedSections(prev => new Set(prev).add('cspm'));

    if (typeof newRange.toWave === 'number') {
      const effectiveMaxWave = maxWaveLimit || maxWave;
      setCspmQuick({
        value: newRange.cspm,
        from: Math.min(newRange.toWave + 1, effectiveMaxWave),
        to: effectiveMaxWave
      });
    }
  }

  // Quick-adds for other sections (same logic as original)
  const [quickAddKill, setQuickAddKill] = useState(() => {
    const lastKillWave = goldEvents.kills.length > 0 ? Math.max(...goldEvents.kills.map(k => k.wave)) : 5;
    const defaultWave = Math.min(lastKillWave + 2, maxWave);
    return { value: 300, wave: defaultWave, timing: waveToTiming(defaultWave) };
  });

  const [quickAddMisc, setQuickAddMisc] = useState(() => {
    const lastMiscWave = goldEvents.miscellaneous.length > 0 ? Math.max(...goldEvents.miscellaneous.map(m => m.wave)) : 5;
    const defaultWave = Math.min(lastMiscWave + 2, maxWave);
    return { value: 100, wave: defaultWave, timing: waveToTiming(defaultWave) };
  });

  // CRUD functions (same logic as original, but update goldEvents instead of inputs)
  function addKill(custom?: Partial<DetailedKill>) {
    const lastKillWave = goldEvents.kills.length > 0 ? Math.max(...goldEvents.kills.map(k => k.wave)) : 5;
    const defaultWave = Math.min(lastKillWave + 2, maxWave);
    const newKill: DetailedKill = {
      id: `kill-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      value: 300,
      wave: defaultWave,
      timing: waveToTiming(defaultWave),
      description: 'Kill',
      ...custom
    };
    onChange({ ...goldEvents, kills: [...goldEvents.kills, newKill].sort((a, b) => a.wave - b.wave) });
    if (goldEvents.kills.length === 0) setExpandedSections(prev => new Set(prev).add('kills'));
  }

  function updateKill(id: string, updates: Partial<DetailedKill>) {
    const updated = goldEvents.kills.map(k => {
      if (k.id !== id) return k;
      const u = { ...k, ...updates };
      if (updates.timing && updates.timing !== k.timing) u.wave = timingToWave(updates.timing, maxWaveLimit || maxWave);
      if (updates.wave && updates.wave !== k.wave) u.timing = waveToTiming(updates.wave);
      return u;
    });
    onChange({ ...goldEvents, kills: updated.sort((a, b) => a.wave - b.wave) });
  }

  function removeKill(id: string) {
    onChange({ ...goldEvents, kills: goldEvents.kills.filter(k => k.id !== id) });
  }

  // Similar CRUD functions for misc, turrets, plates...
  function addMisc(custom?: Partial<DetailedMisc>) {
    const lastMiscWave = goldEvents.miscellaneous.length > 0 ? Math.max(...goldEvents.miscellaneous.map(m => m.wave)) : 5;
    const defaultWave = Math.min(lastMiscWave + 2, maxWave);
    const newMisc: DetailedMisc = {
      id: `misc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      value: 100,
      wave: defaultWave,
      timing: waveToTiming(defaultWave),
      description: 'Miscellaneous Gold',
      ...custom
    };
    onChange({
      ...goldEvents,
      miscellaneous: [...goldEvents.miscellaneous, newMisc].sort((a, b) => a.wave - b.wave)
    });
    if (goldEvents.miscellaneous.length === 0) setExpandedSections(prev => new Set(prev).add('misc'));
  }

  function updateMisc(id: string, updates: Partial<DetailedMisc>) {
    const updated = goldEvents.miscellaneous.map(m => {
      if (m.id !== id) return m;
      const u = { ...m, ...updates };
      if (updates.timing && updates.timing !== m.timing) u.wave = timingToWave(updates.timing, maxWaveLimit || maxWave);
      if (updates.wave && updates.wave !== m.wave) u.timing = waveToTiming(updates.wave);
      return u;
    });
    onChange({ ...goldEvents, miscellaneous: updated.sort((a, b) => a.wave - b.wave) });
  }

  function removeMisc(id: string) {
    onChange({ ...goldEvents, miscellaneous: goldEvents.miscellaneous.filter(m => m.id !== id) });
  }

  // Turret functions
  function addTurret(position: string, tier: string) {
    if (tier !== 'T4') {
      const exists = goldEvents.turrets.find(t => t.position === position && t.type === tier);
      if (exists) return;
    }
    let positionDef = tier === 'T4' && (position === 'Left' || position === 'Right')
      ? TURRET_DEFINITIONS.Nexus
      : TURRET_DEFINITIONS[position];
    if (!positionDef) return;
    const tierDef = positionDef.tiers[tier];
    if (!tierDef) return;

    const newTurret: DetailedTurret = {
      id: `turret-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: tier as 'T1' | 'T2' | 'T3' | 'T4',
      position: position as 'Top' | 'Mid' | 'Bot' | 'Left' | 'Right',
      localGold: tierDef.localGold,
      globalGold: tierDef.globalGold,
      wave: 15,
      timing: waveToTiming(15)
    };
    onChange({ ...goldEvents, turrets: [...goldEvents.turrets, newTurret].sort((a, b) => a.wave - b.wave) });
    if (goldEvents.turrets.length === 0) setExpandedSections(prev => new Set(prev).add('turrets'));
  }

  function updateTurret(id: string, updates: Partial<DetailedTurret>) {
    const updated = goldEvents.turrets.map(t => {
      if (t.id !== id) return t;
      const u = { ...t, ...updates };
      if (updates.timing && updates.timing !== t.timing) u.wave = timingToWave(updates.timing, maxWaveLimit || maxWave);
      if (updates.wave && updates.wave !== t.wave) u.timing = waveToTiming(updates.wave);
      return u;
    });
    onChange({ ...goldEvents, turrets: updated.sort((a, b) => a.wave - b.wave) });
  }

  function removeTurret(id: string) {
    onChange({ ...goldEvents, turrets: goldEvents.turrets.filter(t => t.id !== id) });
  }

  // Plate functions - Season 2025: 5 plates per turret tier (T1, T2, T3), 15 per lane, 45 total
  const [plateInputs, setPlateInputs] = useState<Record<string, Record<string, number>>>({
    Top: { T1: 0, T2: 0, T3: 0 },
    Mid: { T1: 0, T2: 0, T3: 0 },
    Bot: { T1: 0, T2: 0, T3: 0 }
  });
  
  function getPlateCountForTier(position: string, tier: string): number {
    return goldEvents.plates
      .filter(p => p.position === position && p.turretType === tier)
      .reduce((s, p) => s + p.count, 0);
  }

  function updatePlateInput(position: string, tier: string, count: number) {
    const tierTotal = getPlateCountForTier(position, tier);
    const clamped = Math.max(0, Math.min(5 - tierTotal, count)); // 5 plates per tier
    setPlateInputs(prev => ({
      ...prev,
      [position]: { ...prev[position], [tier]: clamped }
    }));
  }

  // Season 2025: 5 plates per turret tier, 15 per lane, 45 total, 120g each
  function addPlate(position: 'Top' | 'Mid' | 'Bot', tier: 'T1' | 'T2' | 'T3', count = 1) {
    const total = goldEvents.plates.reduce((s, p) => s + p.count, 0);
    const tierTotal = getPlateCountForTier(position, tier);
    if (total >= 45 || tierTotal >= 5) return;

    const actual = Math.min(count, 45 - total, 5 - tierTotal);
    if (actual <= 0) return;

    const defaultWave = 8;
    const plate: DetailedPlate = {
      id: `plate-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      turretType: tier,
      position,
      count: actual,
      goldValue: actual * 120, // Season 2025: 120g per plate
      wave: defaultWave,
      timing: waveToTiming(defaultWave)
    };
    onChange({ ...goldEvents, plates: [...goldEvents.plates, plate].sort((a, b) => a.wave - b.wave) });
    if (goldEvents.plates.length === 0) setExpandedSections(prev => new Set(prev).add('plates'));
  }

  function applyPlateInput(position: 'Top' | 'Mid' | 'Bot', tier: 'T1' | 'T2' | 'T3') {
    const requested = plateInputs[position]?.[tier] || 0;
    if (requested <= 0) return;
    const total = goldEvents.plates.reduce((s, p) => s + p.count, 0);
    const tierTotal = getPlateCountForTier(position, tier);
    const count = Math.min(requested, 5 - tierTotal, 45 - total);
    if (count > 0) {
      addPlate(position, tier, count);
      setPlateInputs(prev => ({
        ...prev,
        [position]: { ...prev[position], [tier]: 0 }
      }));
    }
  }

  function updatePlate(id: string, updates: Partial<DetailedPlate>) {
    const updated = goldEvents.plates.map(p => {
      if (p.id !== id) return p;
      const u = { ...p, ...updates };
      if (updates.count !== undefined) u.goldValue = updates.count * 120; // Season 2025: 120g per plate
      if (updates.timing && updates.timing !== p.timing) u.wave = timingToWave(updates.timing, maxWaveLimit || maxWave);
      if (updates.wave && updates.wave !== p.wave) {
        u.timing = waveToTiming(u.wave);
      }
      return u;
    });
    onChange({ ...goldEvents, plates: updated.sort((a, b) => a.wave - b.wave) });
  }

  function removePlate(id: string) {
    onChange({ ...goldEvents, plates: goldEvents.plates.filter(p => p.id !== id) });
  }

  // Totals for display
  const killTotal = goldEvents.kills.reduce((sum, k) => sum + k.value, 0);
  const killXPTotal = goldEvents.kills.reduce((sum, k) => sum + (k.xpValue ?? estimateKillXP(k.wave, activeRole)), 0);
  const turretTotal = goldEvents.turrets.reduce((sum, t) => sum + t.localGold + t.globalGold, 0);
  const plateTotal = goldEvents.plates.reduce((sum, p) => sum + p.goldValue, 0);
  const miscTotal = goldEvents.miscellaneous.reduce((sum, m) => sum + m.value, 0);

  // Quest completion wave and bonus gold calculations (bot lane)
  const questCompletionWave = questCompletion?.withEventsWave ?? questCompletion?.baselineWave ?? null;
  const questRewards = activeRole ? ROLE_QUEST_CONFIG[activeRole]?.rewards : undefined;
  const questKillBonusGold = useMemo(() => {
    if (!questCompletionWave || !questRewards?.bonusGoldPerTakedown) return 0;
    return goldEvents.kills.filter(k => k.wave >= questCompletionWave).length * questRewards.bonusGoldPerTakedown;
  }, [questCompletionWave, questRewards, goldEvents.kills]);
  const questCSBonusGold = useMemo(() => {
    if (!questCompletionWave || !questRewards?.bonusGoldPerMinion) return 0;
    const completionTimeMins = waveToGameTimeMinutes(questCompletionWave);
    const maxTimeMins = waveToGameTimeMinutes(maxWave);
    // Use buildTimeline to account for CSPM ranges
    const tl = buildTimeline(goldEvents.cspm, goldEvents.cspmRanges, maxTimeMins);
    let csAfterQuest = 0;
    for (const seg of tl) {
      const segStart = Math.max(seg.start, completionTimeMins);
      const segEnd = seg.end;
      if (segEnd > segStart) {
        csAfterQuest += seg.cspm * (segEnd - segStart);
      }
    }
    return Math.round(csAfterQuest * questRewards.bonusGoldPerMinion);
  }, [questCompletionWave, questRewards, goldEvents.cspm, goldEvents.cspmRanges, maxWave]);
  const questImmediateGold = (questCompletionWave && questRewards?.immediateGold) ? questRewards.immediateGold : 0;
  const questTotalBonusGold = questImmediateGold + questKillBonusGold + questCSBonusGold;

  // Support item upgrade timeline (mirrors backend WaveGoldCalculator.calculateSupportItemGold)
  const supportItemTimeline = useMemo(() => {
    if (activeRole !== 'sup') return null;
    const procs = Math.min(goldEvents.supportProcsPerMin ?? 3, 3);
    // Tier 1: World Atlas — 18g/min passive + procs × 18g avg per proc
    const tier1GoldPerMin = 18 + procs * 18;
    // Tier 2: Runic Compass — 30g/min passive + procs × 21g avg per proc
    const tier2GoldPerMin = 30 + procs * 21;
    // Active time starts at ~1.5 min (first wave arrival)
    const ACTIVE_START = 1.5;

    const timeToFirst = tier1GoldPerMin > 0 ? 400 / tier1GoldPerMin : Infinity;
    const timeToSecond = tier2GoldPerMin > 0 ? timeToFirst + 800 / tier2GoldPerMin : Infinity;
    // Game time = active time + 1.5 min offset
    const firstUpgradeMin = timeToFirst + ACTIVE_START;
    const secondUpgradeMin = timeToSecond + ACTIVE_START;
    // Total gold/min (average across both tiers)
    const avgGoldPerProc = (18 + 21) / 2;
    return {
      procsPerMin: procs,
      tier1GoldPerMin,
      tier2GoldPerMin,
      goldPerProcT1: 18,
      goldPerProcT2: 21,
      avgGoldPerProc,
      firstUpgradeGameMin: isFinite(firstUpgradeMin) ? firstUpgradeMin : null,
      secondUpgradeGameMin: isFinite(secondUpgradeMin) ? secondUpgradeMin : null,
    };
  }, [activeRole, goldEvents.supportProcsPerMin]);

  const grandTotal = killTotal + turretTotal + plateTotal + miscTotal + questTotalBonusGold;

  // Calculate overall effective CSPM (average over maxWave period)
  const overallEffCSPM = useMemo(() => {
    const maxMinutes = waveToGameTimeMinutes(maxWave);
    return effectiveCSPMUpTo(maxMinutes, goldEvents.cspm, goldEvents.cspmRanges);
  }, [goldEvents.cspm, goldEvents.cspmRanges, maxWave]);

  // Helper functions for gold calculations
  function effectiveCSPMUpTo(minutes: number, baseline: number, ranges?: CSPMRange[]): number {
    if (minutes <= 0) return baseline;
    const tl = buildTimeline(baseline, ranges, minutes);
    let cs = 0;
    for (const seg of tl) cs += seg.cspm * (seg.end - seg.start);
    return +(cs / minutes).toFixed(2);
  }

  function buildTimeline(baseline: number, ranges: CSPMRange[] = [], maxMinutes: number) {
    let timeline = [{ start: 0, end: maxMinutes, cspm: baseline }];
    
    for (const range of ranges) {
      const startMin = waveToGameTimeMinutes(range.fromWave);
      const endMin = range.toWave ? waveToGameTimeMinutes(range.toWave) : maxMinutes;
      
      if (startMin >= maxMinutes) break;

      const newTimeline: { start: number; end: number; cspm: number }[] = [];
      
      for (const segment of timeline) {
        const overlapStart = Math.max(segment.start, startMin);
        const overlapEnd = Math.min(segment.end, Math.min(endMin, maxMinutes));
        
        if (overlapEnd <= overlapStart) {
          newTimeline.push(segment);
          continue;
        }

        if (segment.start < overlapStart) {
          newTimeline.push({ start: segment.start, end: overlapStart, cspm: segment.cspm });
        }
        newTimeline.push({ start: overlapStart, end: overlapEnd, cspm: range.cspm });
        if (overlapEnd < segment.end) {
          newTimeline.push({ start: overlapEnd, end: segment.end, cspm: segment.cspm });
        }
      }
      
      timeline = newTimeline;
    }
    
    return timeline;
  }

  function waveToGameTimeMinutes(wave: number): number {
    return getWaveClearTime(wave) / 60;
  }

  function csGoldUpTo(minutes: number, baseline: number, ranges?: CSPMRange[]): number {
    const tl = buildTimeline(baseline, ranges, minutes);
    let sum = 0;
    for (const seg of tl) {
      sum += goldWindow(seg.cspm, seg.start, seg.end);
    }
    return Math.round(sum);
  }

  function goldWindow(cspm: number, startMin: number, endMin: number): number {
    if (endMin <= startMin) return 0;
    const startGold = calculateGoldFromCS(cspm, Math.max(0, startMin));
    const endGold = calculateGoldFromCS(cspm, Math.max(0, endMin));
    return Math.max(0, endGold - startGold);
  }

  function calculateGoldFromCS(cspm: number, gameTimeMinutes: number): number {
    if (gameTimeMinutes <= 1.5) return 0;
    const effectiveMinutes = gameTimeMinutes - 1.5;
    return effectiveMinutes * cspm * 20;
  }

  function calculatePassiveGold(gameTimeMinutes: number): number {
    const passiveStartMinutes = 100 / 60;
    if (gameTimeMinutes <= passiveStartMinutes) return 0;
    const effectiveMinutes = gameTimeMinutes - passiveStartMinutes;
    return effectiveMinutes * 2.04;
  }

  // Get gold from specific wave numbers
  const getGoldFromSpecificWave = (waveNumber: number): number => {
    const activeWaveProgression = activeChampion === 'primary' ? primaryWaveProgression : opponentWaveProgression;
    
    if (!activeWaveProgression || activeWaveProgression.length === 0) {
      return 0; // No wave data available
    }
    
    // Find the exact wave by wave number
    const targetWave = activeWaveProgression.find(wave => wave.waveNumber === waveNumber);
    
    // Return the total gold from the specific wave
    return targetWave ? (targetWave.totalGold || 0) : 0;
  };

  function formatTiming(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  // Get gold from the first wave where champion reaches level 6
  function getLevel6Gold(): number {
    const activeWaveProgression = activeChampion === 'primary' ? primaryWaveProgression : opponentWaveProgression;
    
    if (!activeWaveProgression || activeWaveProgression.length === 0) {
      return 0; // No wave data available
    }
    
    // Find first wave where champion reaches level 6
    const level6Wave = activeWaveProgression.find(wave => wave.level >= 6);
    return level6Wave ? (level6Wave.totalGold || 0) : 0;
  }

  // Utility to update ranges with tightening
  const updateRangesWith = (mapper: (r: CSPMRange) => CSPMRange | null, targetId?: string) => {
    const next = tightenRanges(
      (goldEvents.cspmRanges || [])
        .map(r => (targetId && r.id === targetId ? mapper(r) ?? r : r))
        .filter(Boolean) as CSPMRange[]
    );
    onChange({ ...goldEvents, cspmRanges: next });
  };

  // Smart validation for gold inputs
  const validateGoldValue = (value: number) => Math.max(100, Math.min(1000, value));

  return (
    <div className={`enhanced-gold-controls ${className}`}>
      {/* Champion Selection and Wave Limit */}
      <div className="egc-champion-selector">
        <div className="egc-champion-toggle">
          <button
            className={`egc-champion-btn ${activeChampion === 'primary' ? 'active' : ''}`}
            data-champion={primaryChampionName.toLowerCase()}
            onClick={() => onActiveChampionChange?.('primary')}
          >
            {primaryChampionName}
          </button>
          <button
            className={`egc-champion-btn ${activeChampion === 'opponent' ? 'active' : ''}`}
            data-champion={opponentChampionName.toLowerCase()}
            onClick={() => onActiveChampionChange?.('opponent')}
          >
            {opponentChampionName}
          </button>
        </div>
        
        {maxWaveLimit && onIncreaseWaveLimit && (
          <div className="egc-wave-limit-controls">
            <span className="egc-wave-limit-label">Wave Limit: {maxWaveLimit}</span>
            <button 
              className="egc-wave-limit-plus-btn"
              onClick={onIncreaseWaveLimit}
              title="Add 20 more waves"
            >
              +20 Waves
            </button>
          </div>
        )}
      </div>

      {/* Summary Bar - Backend will calculate averages */}
      <div className={`egc-summary-bar ${activeChampion}`}>
        <div className="egc-summary-item">
          <img src={gameWideAssets.minionIcon} alt="Minion" className="egc-summary-icon minion-icon" />
          <span className="egc-summary-label">CSPM:</span>
          <span className="egc-summary-value">{overallEffCSPM}</span>
        </div>

        <div className="egc-summary-item">
          <img src={gameWideAssets.damageIconWhite} alt="Kill" className="egc-summary-icon damage-icon" />
          <span className="egc-summary-label">Kills:</span>
          <span className="egc-summary-count">{goldEvents.kills.length}</span>
          <span className="egc-summary-gold">{killTotal}g</span>
        </div>

        <div className="egc-summary-item">
          <img src={gameWideAssets.turretIcon} alt="Turret" className="egc-summary-icon turret-icon" />
          <span className="egc-summary-label">Turrets:</span>
          <span className="egc-summary-count">{goldEvents.turrets.length}</span>
          <span className="egc-summary-gold">{turretTotal}g</span>
        </div>

        <div className="egc-summary-item">
          <img src={gameWideAssets.turretPlateIcon} alt="Turret Plate" className="egc-summary-icon turret-plate-icon" />
          <span className="egc-summary-label">Plates:</span>
          <span className="egc-summary-count">{goldEvents.plates.reduce((sum, p) => sum + p.count, 0)}/45</span>
          <span className="egc-summary-gold">{plateTotal}g</span>
        </div>

        <div className="egc-summary-item">
          <span className="egc-summary-label">Misc:</span>
          <span className="egc-summary-count">{goldEvents.miscellaneous.length}</span>
          <span className="egc-summary-gold">{miscTotal}g</span>
        </div>

        {questTotalBonusGold > 0 && (
          <div className="egc-summary-item" title={`Quest: +${questImmediateGold}g immediate, +${questKillBonusGold}g kills, +${questCSBonusGold}g CS`}>
            <span className="egc-quest-icon-circle egc-summary-quest-icon">R</span>
            <span className="egc-summary-label">Quest:</span>
            <span className="egc-summary-gold">+{questTotalBonusGold}g</span>
          </div>
        )}

        {/* Right side group: Projections and Total */}
        <div className="egc-summary-right-group">
          {/* G@Time Projections */}
          <div className="egc-summary-projections">
            <div className="egc-projection-item">
              <span className="egc-projection-label">g@lvl6</span>
              <span className="egc-projection-value">{getLevel6Gold()}g</span>
            </div>
            <div className="egc-projection-item">
              <span className="egc-projection-label">g@14m</span>
              <span className="egc-projection-value">{getGoldFromSpecificWave(25)}g</span>
            </div>
            <div className="egc-projection-item">
              <span className="egc-projection-label">g@25m</span>
              <span className="egc-projection-value">{getGoldFromSpecificWave(47)}g</span>
            </div>
          </div>

          <div className="egc-summary-total">
            <span className="egc-total-label">Total Extra:</span>
            <span className="egc-total-gold">{grandTotal}g</span>
          </div>
        </div>

     
      </div>

      {/* Sections - Using exact same structure as original but simplified */}
      <div className="egc-detailed-sections">
        {/* CSPM Section */}
        <div className="egc-section">
          <div className="egc-section-header" onClick={() => toggleSection('cspm')}>
            <div className="egc-title-gold-container">
              <div className="egc-title-avg-container">
                <span className="egc-section-title">
                  <div className={`egc-section-icon minion-icon ${activeChampion}`}>
                    <img src={gameWideAssets.minionIcon} alt="CSPM" />
                  </div>
                  CSPM - Creep Score per Minute
                </span>
                <div className="egc-cspm-avg">
                  <span className="egc-avg-label">Average</span>
                  <span className="egc-avg-value">{overallEffCSPM}</span>
                </div>
                {questCSBonusGold > 0 && (
                  <div className="egc-cspm-quest-bonus" title={`+${questRewards!.bonusGoldPerMinion}g per CS after quest completion (Wave ${questCompletionWave})`}>
                    <span className="egc-quest-bonus-tag">+{questCSBonusGold}g <span className="egc-quest-icon-inline">R</span> (+{questRewards!.bonusGoldPerMinion}g/cs)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick add row */}
            <div className="egc-cspm-add-control" onClick={e => e.stopPropagation()}>
              <div className={`egc-add-row egc-cspm-row ${activeChampion}`}>
                <div className="egc-control-container">
                  <span className="egc-control-header">CSPM</span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    step={0.5}
                    className="egc-cspm-input"
                    value={cspmQuick.value}
                    onChange={e =>
                      setCspmQuick(prev => ({ ...prev, value: parseFloat(e.target.value) || prev.value }))
                    }
                  />
                </div>

                <div className="egc-control-container">
                  <span className="egc-control-header">From Wave</span>
                  <input
                    type="number"
                    min={1}
                    max={maxWaveLimit || maxWave}
                    className="egc-wave-input"
                    value={cspmQuick.from}
                    onChange={e => {
                      const effectiveMaxWave = maxWaveLimit || maxWave;
                      const v = Math.max(1, Math.min(effectiveMaxWave, parseInt(e.target.value) || 1));
                      setCspmQuick(prev => ({ ...prev, from: v }));
                    }}
                  />
                </div>

                <div className="egc-control-container">
                  <span className="egc-control-header">From Time</span>
                  <input
                    type="text"
                    className="egc-timing-input"
                    value={waveToTiming(cspmQuick.from)}
                    onChange={e => {
                      const effectiveMaxWave = maxWaveLimit || maxWave;
                      const wave = timingToWave(e.target.value, effectiveMaxWave);
                      const v = Math.max(1, Math.min(effectiveMaxWave, wave));
                      setCspmQuick(prev => ({ ...prev, from: v }));
                    }}
                  />
                </div>

                <div className="egc-control-container">
                  <span className="egc-control-header">To Wave</span>
                  <input
                    type="number"
                    min={1}
                    max={maxWaveLimit || maxWave}
                    className="egc-wave-input"
                    value={cspmQuick.to ?? ''}
                    placeholder={`${maxWaveLimit || maxWave}`}
                    onChange={e => {
                      const raw = e.target.value;
                      const effectiveMaxWave = maxWaveLimit || maxWave;
                      const v = raw
                        ? Math.max(cspmQuick.from, Math.min(effectiveMaxWave, parseInt(raw) || cspmQuick.from))
                        : undefined;
                      setCspmQuick(prev => ({ ...prev, to: v }));
                    }}
                  />
                </div>

                <div className="egc-control-container">
                  <span className="egc-control-header">To Time</span>
                  <input
                    type="text"
                    className="egc-timing-input"
                    value={cspmQuick.to ? waveToTiming(cspmQuick.to) : ''}
                    placeholder={waveToTiming(maxWaveLimit || maxWave)}
                    onChange={e => {
                      const raw = e.target.value;
                      if (raw === '') {
                        setCspmQuick(prev => ({ ...prev, to: undefined }));
                      } else {
                        const effectiveMaxWave = maxWaveLimit || maxWave;
                        const wave = timingToWave(raw, effectiveMaxWave);
                        const v = Math.max(cspmQuick.from, Math.min(effectiveMaxWave, wave));
                        setCspmQuick(prev => ({ ...prev, to: v }));
                      }
                    }}
                  />
                </div>

                <button className="egc-add-btn" onClick={handleAddCSPMRange}>+</button>
              </div>
            </div>
          </div>

          {expandedSections.has('cspm') && (
            <div className="egc-section-content">
              {(goldEvents.cspmRanges && goldEvents.cspmRanges.length > 0) ? (
                goldEvents.cspmRanges.map(r => (
                  <div key={r.id} className="egc-cspm-item">
                    <div className="egc-expanded-item-header">
                      <div className={`egc-expanded-item-icon egc-expanded-cspm-icon ${activeChampion}`}>
                        <img src={gameWideAssets.minionIcon} alt="CSPM" />
                      </div>
                      <span className="egc-expanded-item-name">
                        {r.cspm} CSPM — W{r.fromWave}{r.toWave ? `–W${r.toWave}` : `→W${maxWaveLimit || maxWave}`}
                      </span>
                      <button
                        className="egc-remove-btn"
                        onClick={() =>
                          onChange({ ...goldEvents, cspmRanges: tightenRanges((goldEvents.cspmRanges || []).filter(x => x.id !== r.id)) })
                        }
                      >
                        ×
                      </button>
                    </div>
                    <div className="egc-expanded-wave-time-controls">
                      <div className="egc-control-containers">
                        <div className="egc-control-container">
                          <span className="egc-control-header">CSPM</span>
                          <input
                            type="number"
                            className="egc-cspm-input"
                            min={1}
                            max={50}
                            step={0.5}
                            value={r.cspm}
                            onChange={e =>
                              updateRangesWith(rr => ({ ...rr, cspm: parseFloat(e.target.value) || rr.cspm }), r.id)
                            }
                          />
                        </div>
                        <div className="egc-control-container">
                          <span className="egc-control-header">From Wave</span>
                          <input
                            type="number"
                            className="egc-wave-input"
                            min={1}
                            max={maxWaveLimit || maxWave}
                            value={r.fromWave}
                            onChange={e => {
                              const effectiveMaxWave = maxWaveLimit || maxWave;
                              const v = Math.max(1, Math.min(effectiveMaxWave, parseInt(e.target.value) || 1));
                              updateRangesWith(rr => ({ ...rr, fromWave: v }), r.id);
                            }}
                          />
                        </div>
                        <div className="egc-control-container">
                          <span className="egc-control-header">From Time</span>
                          <input
                            type="text"
                            className="egc-timing-input"
                            value={waveToTiming(r.fromWave)}
                            onChange={e => {
                              const effectiveMaxWave = maxWaveLimit || maxWave;
                              const wave = timingToWave(e.target.value, effectiveMaxWave);
                              const v = Math.max(1, Math.min(effectiveMaxWave, wave));
                              updateRangesWith(rr => ({ ...rr, fromWave: v }), r.id);
                            }}
                          />
                        </div>
                        <div className="egc-control-container">
                          <span className="egc-control-header">To Wave</span>
                          <input
                            type="number"
                            className="egc-wave-input"
                            min={1}
                            max={maxWaveLimit || maxWave}
                            value={r.toWave ?? ''}
                            placeholder={`${maxWaveLimit || maxWave}`}
                            onChange={e => {
                              const raw = e.target.value;
                              const effectiveMaxWave = maxWaveLimit || maxWave;
                              const v = raw
                                ? Math.max(r.fromWave, Math.min(effectiveMaxWave, parseInt(raw) || r.fromWave))
                                : undefined;
                              updateRangesWith(rr => ({ ...rr, toWave: v }), r.id);
                            }}
                          />
                        </div>
                        <div className="egc-control-container">
                          <span className="egc-control-header">To Time</span>
                          <input
                            type="text"
                            className="egc-timing-input"
                            value={r.toWave ? waveToTiming(r.toWave) : ''}
                            placeholder={waveToTiming(maxWaveLimit || maxWave)}
                            onChange={e => {
                              const raw = e.target.value;
                              if (raw === '') {
                                updateRangesWith(rr => ({ ...rr, toWave: undefined }), r.id);
                              } else {
                                const effectiveMaxWave = maxWaveLimit || maxWave;
                                const wave = timingToWave(raw, effectiveMaxWave);
                                const v = Math.max(r.fromWave, Math.min(effectiveMaxWave, wave));
                                updateRangesWith(rr => ({ ...rr, toWave: v }), r.id);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="egc-empty-state">No CSPM ranges yet. Use the quick add above.</div>
              )}
            </div>
          )}
        </div>

        {/* Support Item Section - only shown for support role */}
        {activeRole === 'sup' && (
          <div className="egc-section">
            <div className="egc-section-header" onClick={() => toggleSection('supportItem')}>
              <span className="egc-collapse-arrow">{expandedSections.has('supportItem') ? '▼' : '▶'}</span>
              <div className="egc-title-gold-container">
                <div className="egc-title-avg-container">
                  <span className="egc-section-title">
                    <div className={`egc-section-icon ${activeChampion}`}>
                      <img src="/Images/sup_icon.png" alt="Support" style={{ width: 16, height: 16 }} />
                    </div>
                    Support Item — Shared Riches
                    {supportItemTimeline && supportItemTimeline.secondUpgradeGameMin && (
                      <span style={{ fontWeight: 'normal', opacity: 0.8, marginLeft: 8, fontSize: '0.8rem' }}>
                        Bounty @ {formatTiming(Math.round(supportItemTimeline.secondUpgradeGameMin * 60))}
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="egc-cspm-add-control" onClick={e => e.stopPropagation()}>
                <div className={`egc-add-row egc-cspm-row ${activeChampion}`}>
                  <div className="egc-control-container" title="How many Shared Riches charge procs the support lands per minute. Charges recharge every 20s (max 3/min). Each proc grants gold: minion kill (15–20g) or champion/structure hit (20–24g). Higher = faster item upgrades.">
                    <span className="egc-control-header">Procs / min</span>
                    <input
                      type="number"
                      min={0}
                      max={3}
                      step={0.5}
                      className="egc-cspm-input"
                      value={goldEvents.supportProcsPerMin ?? 3}
                      onChange={e => {
                        const v = Math.max(0, Math.min(3, parseFloat(e.target.value) || 0));
                        onChange({ ...goldEvents, supportProcsPerMin: v });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {expandedSections.has('supportItem') && supportItemTimeline && (
              <div className="egc-section-content egc-quest-details">
                {/* Upgrade timeline */}
                <div className="egc-quest-info-row">
                  <span className="egc-quest-label">World Atlas → Runic Compass (400g):</span>
                  <span className="egc-quest-value">
                    {supportItemTimeline.firstUpgradeGameMin != null
                      ? formatTiming(Math.round(supportItemTimeline.firstUpgradeGameMin * 60))
                      : 'Never'}
                  </span>
                </div>
                <div className="egc-quest-info-row">
                  <span className="egc-quest-label">Runic Compass → Bounty of Worlds (1200g):</span>
                  <span className="egc-quest-value egc-quest-highlight">
                    {supportItemTimeline.secondUpgradeGameMin != null
                      ? formatTiming(Math.round(supportItemTimeline.secondUpgradeGameMin * 60))
                      : 'Never'}
                  </span>
                </div>

                {/* Gold generation rates */}
                <div className="egc-quest-info-row">
                  <span className="egc-quest-label">Gold / min (World Atlas):</span>
                  <span className="egc-quest-value">{supportItemTimeline.tier1GoldPerMin}g/min</span>
                </div>
                <div className="egc-quest-info-row">
                  <span className="egc-quest-label">Gold / min (Runic Compass):</span>
                  <span className="egc-quest-value">{supportItemTimeline.tier2GoldPerMin}g/min</span>
                </div>

                {/* Assumption note */}
                <div className="egc-quest-info-row egc-quest-point-sources">
                  <span className="egc-quest-label">Assumptions:</span>
                  <div className="egc-quest-sources-list">
                    <span>Item passive: 3g/10s (World Atlas), 5g/10s (Runic Compass)</span>
                    <span>Avg gold per charge proc: ~{supportItemTimeline.goldPerProcT1}g (World Atlas), ~{supportItemTimeline.goldPerProcT2}g (Runic Compass)</span>
                    <span style={{ opacity: 0.7, fontStyle: 'italic' }}>
                      Proc gold averaged between minion kill ({`15→20g`}) and champion/structure hit ({`20→24g`})
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Kills Section - Simplified version */}
        <div className="egc-section">
          <div className="egc-section-header" onClick={() => toggleSection('kills')}>
            <span className="egc-section-title">
              <div className={`egc-section-icon damage-icon ${activeChampion}`}>
                <img src={gameWideAssets.damageIconWhite} alt="Kill" />
              </div>
              Kills ({goldEvents.kills.length}) - {killTotal}g{questKillBonusGold > 0 ? <span className="egc-quest-bonus-tag" title={`+${questRewards!.bonusGoldPerTakedown}g per kill after quest completion`}> (+{questKillBonusGold}g <span className="egc-quest-icon-inline">R</span>)</span> : ''} / <span title={`Assumes equal-level opponent, no comeback bonus/penalty. ${(activeRole === 'bot' || activeRole === 'sup') ? 'Shared XP (duo lane, total ÷ 2).' : 'Solo kill XP.'}`}>{killXPTotal} XP</span>
            </span>
            <div className="egc-kills-add-control" onClick={e => e.stopPropagation()}>
              <div className={`egc-add-row ${activeChampion}`}>
                <div className="egc-add-row-container">
                  <div className="egc-control-container">
                    <span className="egc-control-header">Kill Value</span>
                    <div className="egc-gold-input-group">
                      <input
                        type="number"
                        value={quickAddKill.value}
                        onChange={e =>
                          setQuickAddKill(prev => ({ ...prev, value: parseInt(e.target.value) || 0 }))
                        }
                        onBlur={e =>
                          setQuickAddKill(prev => ({
                            ...prev,
                            value: validateGoldValue(parseInt(e.target.value) || 300)
                          }))
                        }
                        className="egc-quick-gold-input"
                        placeholder="300"
                      />
                      <span className="egc-gold-unit">g</span>
                    </div>
                  </div>
                  <button
                    className="egc-add-btn"
                    onClick={() =>
                      addKill({
                        value: validateGoldValue(quickAddKill.value),
                        wave: quickAddKill.wave,
                        timing: quickAddKill.timing
                      })
                    }
                  >
                    +
                  </button>
                </div>

                <div className="egc-wave-time-controls">
                  <div className="egc-control-containers">
                    <div className="egc-control-container">
                      <span className="egc-control-header">Wave</span>
                      <input
                        type="number"
                        value={quickAddKill.wave}
                        onChange={e => {
                          const w = Math.max(1, Math.min(maxWave, parseInt(e.target.value) || 1));
                          setQuickAddKill(prev => ({ ...prev, wave: w, timing: waveToTiming(w) }));
                        }}
                        className="egc-quick-wave-input"
                        min={1}
                        max={maxWave}
                        placeholder="W"
                      />
                    </div>
                    <div className="egc-control-container">
                      <span className="egc-control-header">Time</span>
                      <input
                        type="text"
                        value={quickAddKill.timing}
                        onChange={e =>
                          setQuickAddKill(prev => ({ ...prev, timing: e.target.value, wave: timingToWave(e.target.value, maxWaveLimit || maxWave) }))
                        }
                        className="egc-quick-timing-input"
                        placeholder="2:15"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {expandedSections.has('kills') && (
            <div className="egc-section-content">
              {goldEvents.kills.length === 0 ? (
                <div className="egc-empty-state">No kills added yet. Click + to add a kill.</div>
              ) : (
                goldEvents.kills.map(kill => {
                  const killXP = kill.xpValue ?? estimateKillXP(kill.wave, activeRole);
                  const questCfg = activeRole ? ROLE_QUEST_CONFIG[activeRole] : undefined;
                  const killQuestPts = questCfg?.pointsPerKill ?? 0;
                  // Check if quest is already completed at this kill's wave
                  // Use wave progression data if available, else estimate from config
                  const activeWaves = activeChampion === 'primary' ? primaryWaveProgression : opponentWaveProgression;
                  const killWaveData = activeWaves?.find((w: any) => w.waveNumber === kill.wave);
                  const questAlreadyDone = killWaveData?.questCompleted && !killWaveData?.questCompletedThisWave;
                  // Bot quest: +50g per kill after quest completion
                  const killQuestGoldBonus = (questCompletionWave && kill.wave >= questCompletionWave && questRewards?.bonusGoldPerTakedown) ? questRewards.bonusGoldPerTakedown : 0;
                  return (
                  <div key={kill.id} className="egc-kill-item">
                    <div className="egc-expanded-item-header">
                      <div className={`egc-expanded-item-icon egc-expanded-kill-icon ${activeChampion}`}>
                        <img src={gameWideAssets.damageIconWhite} alt="Kill" />
                      </div>
                      <span className="egc-expanded-item-name">
                        Champion Kill - {kill.value}g{killQuestGoldBonus > 0 ? <span className="egc-quest-bonus-tag"> (+{killQuestGoldBonus}g <span className="egc-quest-icon-inline">R</span>)</span> : ''} / {killXP} XP{killQuestPts > 0 && !questAlreadyDone ? ` / ${killQuestPts} QP` : ''}
                      </span>
                      <button className="egc-remove-btn" onClick={() => removeKill(kill.id)}>×</button>
                    </div>
                    <div className="egc-kill-controls">
                      <div className="egc-expanded-wave-time-controls">
                        <div className="egc-control-containers">
                          <div className="egc-control-container">
                            <span className="egc-control-header">Kill Value</span>
                            <div className="egc-gold-input-group">
                              <input
                                type="number"
                                value={kill.value}
                                onChange={e => updateKill(kill.id, { value: parseInt(e.target.value) || 0 })}
                                onBlur={e =>
                                  updateKill(kill.id, { value: validateGoldValue(parseInt(e.target.value) || 300) })
                                }
                                className="egc-kill-value"
                                placeholder="300"
                              />
                              <span className="egc-gold-unit">g</span>
                            </div>
                          </div>
                          <div className="egc-control-container" title={`${(activeRole === 'bot' || activeRole === 'sup') ? 'Shared (duo) kill XP' : 'Solo kill XP'} assuming equal-level opponent. Comeback bonus/penalty not applied.`}>
                            <span className="egc-control-header">XP</span>
                            <div className="egc-gold-input-group">
                              <span className="egc-xp-display">{killXP}</span>
                              <span className="egc-gold-unit">xp</span>
                            </div>
                          </div>
                          {killQuestPts > 0 && (
                          <div className="egc-control-container" title={questAlreadyDone ? 'Quest already completed — no more quest points' : `Quest points from this kill (assumes in own lane)`}>
                            <span className="egc-control-header">QP</span>
                            <div className="egc-gold-input-group">
                              <span className={`egc-xp-display ${questAlreadyDone ? 'egc-quest-done' : 'egc-quest-active'}`}>{questAlreadyDone ? '—' : killQuestPts}</span>
                            </div>
                          </div>
                          )}
                          <div className="egc-control-container">
                            <span className="egc-control-header">Wave</span>
                            <input
                              type="number"
                              value={kill.wave}
                              onChange={e =>
                                updateKill(kill.id, {
                                  wave: Math.max(1, Math.min(maxWave, parseInt(e.target.value) || 1))
                                })
                              }
                              className="egc-wave-input"
                              min={1}
                              max={maxWave}
                            />
                          </div>
                          <div className="egc-control-container">
                            <span className="egc-control-header">Time</span>
                            <input
                              type="text"
                              value={kill.timing}
                              onChange={e => updateKill(kill.id, { timing: e.target.value })}
                              className="egc-timing-input"
                              placeholder="2:15"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Turrets Section */}
        <div className="egc-section">
          <div className="egc-section-header" onClick={() => toggleSection('turrets')}>
            <span className="egc-section-title">
              <div className={`egc-section-icon turret-icon ${activeChampion}`}>
                <img src={gameWideAssets.turretIcon} alt="Turret" />
              </div>
              Turrets ({goldEvents.turrets.length}) - {turretTotal}g
            </span>

            <div className="egc-turrets-add-control" onClick={e => e.stopPropagation()}>
              <div className={`egc-add-row ${activeChampion}`}>
                <div className="egc-add-row-container">
                  <div className={`egc-turrets-left ${activeChampion}`}>
                    {Object.entries(TURRET_DEFINITIONS)
                      .filter(([pos]) => pos !== 'Nexus')
                      .map(([position, positionDef]) => (
                        <div key={position} className="egc-turret-row">
                          <span className="egc-turret-position-label">{position}</span>
                          {Object.entries(positionDef.tiers).map(([tier, tierDef]) => {
                            const existing = goldEvents.turrets.find(t => t.position === position && t.type === tier);
                            const canAdd = !existing;
                            const totalGold = tierDef.localGold + tierDef.globalGold;
                            return (
                              <div key={tier} className="egc-turret-tier-group">
                                <button
                                  className={`egc-turret-tier-btn ${!canAdd ? 'disabled' : ''}`}
                                  onClick={() => canAdd && addTurret(position, tier)}
                                  disabled={!canAdd}
                                  title={`${tierDef.label} Turret ${position} - ${totalGold}g`}
                                >
                                  {tier}
                                </button>
                                <span className="egc-turret-gold-value">{totalGold}g</span>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    <div className="egc-turret-row">
                      <span className="egc-turret-position-label">Nexus</span>
                      {['Left', 'Right'].map(side => {
                        const def = TURRET_DEFINITIONS.Nexus.tiers.T4;
                        const total = def.localGold + def.globalGold;
                        const count = goldEvents.turrets.filter(t => t.position === side && t.type === 'T4').length;
                        return (
                          <div key={side} className="egc-turret-tier-group">
                            <button
                              className="egc-turret-tier-btn"
                              onClick={() => addTurret(side, 'T4')}
                              title={`Nexus Turret ${side} - ${total}g ${count > 0 ? `(${count} added)` : ''}`}
                            >
                              {side === 'Left' ? 'L' : 'R'}
                            </button>
                            <span className="egc-turret-gold-value">{total}g</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="egc-wave-time-controls">
                  <div className="egc-control-containers">
                    <div className="egc-control-container">
                      <span className="egc-control-header">Wave</span>
                      <input
                        type="number"
                        className="egc-quick-wave-input"
                        defaultValue={15}
                        min={1}
                        max={maxWave}
                        placeholder="W"
                        onChange={e => {
                          const w = Math.max(1, Math.min(maxWave, parseInt(e.target.value) || 1));
                          const timeInput = e.target.parentElement?.parentElement?.querySelector('.egc-quick-timing-input') as HTMLInputElement;
                          if (timeInput) {
                            timeInput.value = waveToTiming(w);
                          }
                        }}
                      />
                    </div>
                    <div className="egc-control-container">
                      <span className="egc-control-header">Time</span>
                      <input
                        type="text"
                        className="egc-quick-timing-input"
                        defaultValue={waveToTiming(15)}
                        placeholder="8:00"
                        onChange={e => {
                          const waveInput = e.target.parentElement?.parentElement?.querySelector('.egc-quick-wave-input') as HTMLInputElement;
                          if (waveInput) {
                            const wave = timingToWave(e.target.value, maxWaveLimit || maxWave);
                            waveInput.value = wave.toString();
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {expandedSections.has('turrets') && (
            <div className="egc-section-content">
              {goldEvents.turrets.length === 0 ? (
                <div className="egc-empty-state">No turrets added yet. Use the buttons above to add turrets.</div>
              ) : (
                goldEvents.turrets.map(turret => {
                  const tQuestCfg = activeRole ? ROLE_QUEST_CONFIG[activeRole] : undefined;
                  const isOwnLane = tQuestCfg && turret.position === tQuestCfg.lanePosition;
                  const turretQP = tQuestCfg ? (isOwnLane ? tQuestCfg.pointsPerTurretOwn : tQuestCfg.pointsPerTurretOther) : 0;
                  const turretWaveData = (activeChampion === 'primary' ? primaryWaveProgression : opponentWaveProgression)?.find((w: any) => w.waveNumber === turret.wave);
                  const tQuestDone = turretWaveData?.questCompleted && !turretWaveData?.questCompletedThisWave;
                  return (
                  <div key={turret.id} className="egc-turret-item">
                    <div className="egc-expanded-item-header">
                      <div className={`egc-expanded-item-icon egc-expanded-turret-icon ${activeChampion}`}>
                        <img src={gameWideAssets.turretIcon} alt="Turret" />
                      </div>
                      <span className="egc-expanded-item-name">
                        {turret.type} Turret {turret.position} - {turret.localGold + turret.globalGold}g{turretQP > 0 && !tQuestDone ? ` / ${turretQP} QP` : ''}
                      </span>
                      <button className="egc-remove-btn" onClick={() => removeTurret(turret.id)}>×</button>
                    </div>
                    <div className="egc-turret-controls">
                      <div className="egc-expanded-wave-time-controls">
                        <div className="egc-control-containers">
                          {turretQP > 0 && (
                          <div className="egc-control-container" title={tQuestDone ? 'Quest already completed — no more quest points' : `${isOwnLane ? 'Own lane' : 'Other lane'} turret (${turretQP} quest points)`}>
                            <span className="egc-control-header">QP</span>
                            <div className="egc-gold-input-group">
                              <span className={`egc-xp-display ${tQuestDone ? 'egc-quest-done' : 'egc-quest-active'}`}>{tQuestDone ? '—' : turretQP}</span>
                            </div>
                          </div>
                          )}
                          <div className="egc-control-container">
                            <span className="egc-control-header">Wave</span>
                            <input
                              type="number"
                              value={turret.wave}
                              onChange={e =>
                                updateTurret(turret.id, {
                                  wave: Math.max(1, Math.min(maxWave, parseInt(e.target.value) || 1))
                                })
                              }
                              className="egc-wave-input"
                              min={1}
                              max={maxWave}
                            />
                          </div>
                          <div className="egc-control-container">
                            <span className="egc-control-header">Time</span>
                            <input
                              type="text"
                              value={turret.timing}
                              onChange={e => updateTurret(turret.id, { timing: e.target.value })}
                              className="egc-timing-input"
                              placeholder="12:30"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Plates Section */}
        <div className="egc-section">
          <div className="egc-section-header" onClick={() => toggleSection('plates')}>
            <span className="egc-section-title">
              <div className={`egc-section-icon turret-plate-icon ${activeChampion}`}>
                <img src={gameWideAssets.turretPlateIcon} alt="Turret Plate" />
              </div>
              Plates ({goldEvents.plates.reduce((sum, p) => sum + p.count, 0)}/45) - {plateTotal}g
            </span>
            <div className="egc-plates-add-control" onClick={e => e.stopPropagation()}>
              <div className={`egc-add-row ${activeChampion}`}>
                <div className="egc-add-row-container">
                  <div className={`egc-turrets-left ${activeChampion}`}>
                    {PLATE_POSITIONS.map(position => {
                      const laneTotal = goldEvents.plates
                        .filter(p => p.position === position)
                        .reduce((sum, p) => sum + p.count, 0);
                      
                      return (
                        <div key={position} className="egc-turret-row">
                          <span className="egc-turret-position-label">{position}</span>
                          {(['T1', 'T2', 'T3'] as const).map(tier => {
                            const tierTotal = getPlateCountForTier(position, tier);
                            const totalPlates = goldEvents.plates.reduce((sum, p) => sum + p.count, 0);
                            const remainingTier = Math.max(0, 5 - tierTotal);
                            const remainingTotal = Math.max(0, 45 - totalPlates);
                            const hardMax = Math.min(remainingTier, remainingTotal);
                            const inputVal = Math.min(plateInputs[position]?.[tier] || 0, hardMax);
                            const disabled = inputVal <= 0 || hardMax <= 0;
                            const canAddAll = hardMax > 0;

                            return (
                              <div key={tier} className="egc-turret-tier-group">
                                <button
                                  className={`egc-plate-tier-label-btn ${!canAddAll ? 'disabled' : ''}`}
                                  onClick={() => canAddAll && addPlate(position, tier, hardMax)}
                                  disabled={!canAddAll}
                                  title={
                                    !canAddAll
                                      ? `${position} ${tier} is maxed (${tierTotal}/5)`
                                      : `Add all ${hardMax} remaining plate${hardMax !== 1 ? 's' : ''} to ${position} ${tier}`
                                  }
                                >
                                  {tier}
                                </button>
                                <input
                                  type="number"
                                  className="egc-plate-tier-input"
                                  value={inputVal}
                                  min={0}
                                  max={hardMax}
                                  placeholder="0"
                                  onChange={e => {
                                    const v = Math.max(0, Math.min(hardMax, parseInt(e.target.value) || 0));
                                    updatePlateInput(position, tier, v);
                                  }}
                                />
                                <button
                                  className={`egc-turret-tier-btn ${hardMax <= 0 ? 'disabled' : ''}`}
                                  onClick={() => !disabled && applyPlateInput(position, tier)}
                                  disabled={disabled}
                                  title={
                                    hardMax <= 0
                                      ? `${position} ${tier} is maxed (${tierTotal}/5)`
                                      : `Add ${inputVal || 1} plate${(inputVal || 1) !== 1 ? 's' : ''} to ${position} ${tier} (120g each)`
                                  }
                                >
                                  +
                                </button>
                                <span className="egc-turret-gold-value">{tierTotal}/5</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="egc-wave-time-controls">
                  <div className="egc-control-containers">
                    <div className="egc-control-container">
                      <span className="egc-control-header">Wave</span>
                      <input
                        type="number"
                        className="egc-quick-wave-input"
                        defaultValue={8}
                        min={1}
                        max={maxWave}
                        placeholder="W"
                        onChange={e => {
                          const w = Math.max(1, Math.min(maxWave, parseInt(e.target.value) || 1));
                          const timeInput = e.target.parentElement?.parentElement?.querySelector('.egc-quick-timing-input') as HTMLInputElement;
                          if (timeInput) {
                            timeInput.value = waveToTiming(w);
                          }
                        }}
                      />
                    </div>
                    <div className="egc-control-container">
                      <span className="egc-control-header">Time</span>
                      <input
                        type="text"
                        className="egc-quick-timing-input"
                        defaultValue={waveToTiming(8)}
                        placeholder="5:30"
                        onChange={e => {
                          const waveInput = e.target.parentElement?.parentElement?.querySelector('.egc-quick-wave-input') as HTMLInputElement;
                          if (waveInput) {
                            const wave = timingToWave(e.target.value, maxWave);
                            const clampedWave = Math.max(1, Math.min(maxWave, wave));
                            waveInput.value = clampedWave.toString();
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {expandedSections.has('plates') && (
            <div className="egc-section-content">
              {goldEvents.plates.length === 0 ? (
                <div className="egc-empty-state">No plates added yet. Use the buttons above to add plates.</div>
              ) : (
                goldEvents.plates.map(plate => {
                  const pQuestCfg = activeRole ? ROLE_QUEST_CONFIG[activeRole] : undefined;
                  const isOwnLane = pQuestCfg && plate.position === pQuestCfg.lanePosition;
                  const plateQPPerPlate = pQuestCfg ? (isOwnLane ? pQuestCfg.pointsPerPlateOwn : pQuestCfg.pointsPerPlateOther) : 0;
                  const totalPlateQP = plateQPPerPlate * plate.count;
                  const plateWaveData = (activeChampion === 'primary' ? primaryWaveProgression : opponentWaveProgression)?.find((w: any) => w.waveNumber === plate.wave);
                  const pQuestDone = plateWaveData?.questCompleted && !plateWaveData?.questCompletedThisWave;
                  return (
                  <div key={plate.id} className="egc-plate-item">
                    <div className="egc-expanded-item-header">
                      <div className={`egc-expanded-item-icon egc-expanded-plate-icon ${activeChampion}`}>
                        <img src={gameWideAssets.turretPlateIcon} alt="Plate" />
                      </div>
                      <span className="egc-expanded-item-name">{plate.position} {plate.turretType} Plates ({plate.count}) - {plate.goldValue}g{totalPlateQP > 0 && !pQuestDone ? ` / ${totalPlateQP} QP` : ''}</span>
                      <button className="egc-remove-btn" onClick={() => removePlate(plate.id)}>×</button>
                    </div>
                    <div className="egc-plate-controls">
                      <div className="egc-expanded-wave-time-controls">
                        <div className="egc-control-containers">
                          {totalPlateQP > 0 && (
                          <div className="egc-control-container" title={pQuestDone ? 'Quest already completed — no more quest points' : `${isOwnLane ? 'Own lane' : 'Other lane'} plates (${plateQPPerPlate} QP × ${plate.count} = ${totalPlateQP} quest points)`}>
                            <span className="egc-control-header">QP</span>
                            <div className="egc-gold-input-group">
                              <span className={`egc-xp-display ${pQuestDone ? 'egc-quest-done' : 'egc-quest-active'}`}>{pQuestDone ? '—' : totalPlateQP}</span>
                            </div>
                          </div>
                          )}
                          <div className="egc-control-container">
                            <span className="egc-control-header">Plates</span>
                            <input
                              type="number"
                              value={plate.count}
                              onChange={e => updatePlate(plate.id, { count: Math.max(1, Math.min(5, parseInt(e.target.value) || 1)) })}
                              className="egc-count-input"
                              min={1}
                              max={5}
                            />
                          </div>
                          <div className="egc-control-container">
                            <span className="egc-control-header">Wave</span>
                            <input
                              type="number"
                              value={plate.wave}
                              onChange={e => {
                                const wave = Math.max(1, Math.min(maxWave, parseInt(e.target.value) || 1));
                                updatePlate(plate.id, { wave });
                              }}
                              className="egc-wave-input"
                              min={1}
                              max={maxWave}
                              title="Plates available until turret is destroyed"
                            />
                          </div>
                          <div className="egc-control-container">
                            <span className="egc-control-header">Time</span>
                            <input
                              type="text"
                              value={plate.timing}
                              onChange={e => updatePlate(plate.id, { timing: e.target.value })}
                              className="egc-timing-input"
                              placeholder="5:30"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Misc Section */}
        <div className="egc-section">
          <div className="egc-section-header" onClick={() => toggleSection('misc')}>
            <span className="egc-section-title">
              <div className={`egc-section-icon misc-icon ${activeChampion}`}>
                ?
              </div>
              Miscellaneous Gold ({goldEvents.miscellaneous.length}) - {miscTotal}g
            </span>
            <div className="egc-misc-add-control" onClick={e => e.stopPropagation()}>
              <div className={`egc-add-row ${activeChampion}`}>
                <div className="egc-add-row-container">
                  <div className="egc-control-container">
                    <span className="egc-control-header">Gold Value</span>
                    <div className="egc-gold-input-group">
                      <input
                        type="number"
                        value={quickAddMisc.value}
                        onChange={e => setQuickAddMisc(prev => ({ ...prev, value: parseInt(e.target.value) || 0 }))}
                        onBlur={e =>
                          setQuickAddMisc(prev => ({
                            ...prev,
                            value: validateGoldValue(parseInt(e.target.value) || 100)
                          }))
                        }
                        className="egc-quick-gold-input"
                        placeholder="100"
                      />
                      <span className="egc-gold-unit">g</span>
                    </div>
                  </div>
                  <button
                    className="egc-add-btn"
                    onClick={() =>
                      addMisc({
                        value: validateGoldValue(quickAddMisc.value),
                        wave: quickAddMisc.wave,
                        timing: quickAddMisc.timing
                      })
                    }
                  >
                    +
                  </button>
                </div>

                <div className="egc-wave-time-controls">
                  <div className="egc-control-containers">
                    <div className="egc-control-container">
                      <span className="egc-control-header">Wave</span>
                      <input
                        type="number"
                        value={quickAddMisc.wave}
                        onChange={e => {
                          const w = Math.max(1, Math.min(maxWave, parseInt(e.target.value) || 1));
                          setQuickAddMisc(prev => ({ ...prev, wave: w, timing: waveToTiming(w) }));
                        }}
                        className="egc-quick-wave-input"
                        min={1}
                        max={maxWave}
                        placeholder="W"
                      />
                    </div>
                    <div className="egc-control-container">
                      <span className="egc-control-header">Time</span>
                      <input
                        type="text"
                        value={quickAddMisc.timing}
                        onChange={e =>
                          setQuickAddMisc(prev => ({ ...prev, timing: e.target.value, wave: timingToWave(e.target.value, maxWaveLimit || maxWave) }))
                        }
                        className="egc-quick-timing-input"
                        placeholder="2:15"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {expandedSections.has('misc') && (
            <div className="egc-section-content">
              {goldEvents.miscellaneous.length === 0 ? (
                <div className="egc-empty-state">No miscellaneous gold added yet. Click + to add gold.</div>
              ) : (
                goldEvents.miscellaneous.map(misc => (
                  <div key={misc.id} className="egc-misc-item">
                    <div className="egc-expanded-item-header">
                      <div className={`egc-expanded-item-icon egc-expanded-misc-icon ${activeChampion}`}>
                        ?
                      </div>
                      <span className="egc-expanded-item-name">Miscellaneous Gold - {misc.value}g</span>
                      <button className="egc-remove-btn" onClick={() => removeMisc(misc.id)}>×</button>
                    </div>

                    <div className="egc-expanded-wave-time-controls">
                      <div className="egc-control-containers">
                        <div className="egc-control-container">
                          <span className="egc-control-header">Gold</span>
                          <input
                            type="number"
                            value={misc.value}
                            onChange={e => updateMisc(misc.id, { value: Math.max(0, parseInt(e.target.value) || 0) })}
                            className="egc-gold-input"
                            min={0}
                          />
                        </div>
                        <div className="egc-control-container">
                          <span className="egc-control-header">Wave</span>
                          <input
                            type="number"
                            value={misc.wave}
                            onChange={e =>
                              updateMisc(misc.id, {
                                wave: Math.max(1, Math.min(maxWave, parseInt(e.target.value) || 1))
                              })
                            }
                            className="egc-wave-input"
                            min={1}
                            max={maxWave}
                          />
                        </div>
                        <div className="egc-control-container">
                          <span className="egc-control-header">Time</span>
                          <input
                            type="text"
                            value={misc.timing}
                            onChange={e => updateMisc(misc.id, { timing: e.target.value })}
                            className="egc-timing-input"
                            placeholder="5:30"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Role Quest Progress */}
        {questCompletion && activeRole && ROLE_QUEST_CONFIG[activeRole] && (() => {
          const questConfig = ROLE_QUEST_CONFIG[activeRole]!;
          const qc = questCompletion;
          // Determine the completion wave to show (with-events if events exist, otherwise baseline)
          const completionWave = qc.withEventsWave ?? qc.baselineWave;
          const completionTime = completionWave ? waveToTiming(completionWave) : null;
          const hasEvents = qc.totalEventQP > 0;

          return (
            <div className="egc-section egc-quest-section">
              <div className="egc-section-header" onClick={() => toggleSection('quest')}>
                <span className="egc-section-title">
                  <div className={`egc-section-icon egc-quest-section-icon ${activeChampion}`}>
                    <span className="egc-quest-icon-circle">R</span>
                  </div>
                  <span title="Assumes champion stays in own lane for quest points. Passive: 8 pts/5s in lane starting at 1:05.">
                    Role Quest ({ROLE_LABELS[activeRole]})
                    {completionWave
                      ? ` — Wave ${completionWave} (${completionTime})`
                      : ' — Does not complete'}
                    {hasEvents && qc.wavesSaved > 0 ? ` · ${qc.wavesSaved}w earlier` : ''}
                  </span>
                </span>
              </div>

              {expandedSections.has('quest') && (
                <div className="egc-section-content egc-quest-details">
                  {/* Completion timing */}
                  <div className="egc-quest-info-row">
                    <span className="egc-quest-label">Baseline (passive + CS):</span>
                    <span className="egc-quest-value">
                      {qc.baselineWave ? `Wave ${qc.baselineWave} (${qc.baselineTime})` : 'N/A'}
                    </span>
                  </div>
                  {hasEvents && (
                    <div className="egc-quest-info-row">
                      <span className="egc-quest-label">With events:</span>
                      <span className="egc-quest-value egc-quest-highlight">
                        {qc.withEventsWave ? `Wave ${qc.withEventsWave} (${qc.withEventsTime})` : 'N/A'}
                        {qc.wavesSaved > 0 ? ` — ${qc.wavesSaved} wave${qc.wavesSaved !== 1 ? 's' : ''} earlier` : ''}
                      </span>
                    </div>
                  )}

                  {/* Event QP breakdown */}
                  {hasEvents && (
                    <div className="egc-quest-info-row egc-quest-point-sources">
                      <span className="egc-quest-label">Event QP ({qc.totalEventQP} total):</span>
                      <div className="egc-quest-sources-list">
                        {qc.eventKillQP > 0 && <span>Kills ({qc.killCount}): +{qc.eventKillQP} QP</span>}
                        {qc.eventTurretQP > 0 && <span>Turrets ({qc.turretCount}): +{qc.eventTurretQP} QP</span>}
                        {qc.eventPlateQP > 0 && <span>Plates ({qc.plateCount}): +{qc.eventPlateQP} QP</span>}
                      </div>
                    </div>
                  )}

                  {/* Rewards */}
                  <div className="egc-quest-info-row">
                    <span className="egc-quest-label">Rewards:</span>
                    <span className="egc-quest-value">{questConfig.rewards.description}</span>
                  </div>

                  {/* Point rates */}
                  <div className="egc-quest-info-row egc-quest-point-sources">
                    <span className="egc-quest-label">Point Rates:</span>
                    <div className="egc-quest-sources-list">
                      <span>Passive: 8 pts / 5s in lane (from 1:05)</span>
                      <span>CS: {questConfig.pointsPerCS} pts / minion</span>
                      <span>Kills: {questConfig.pointsPerKill} pts / takedown</span>
                      <span>Turret: {questConfig.pointsPerTurretOwn} pts own lane / {questConfig.pointsPerTurretOther} pts other</span>
                      <span>Plate: {questConfig.pointsPerPlateOwn} pts own lane / {questConfig.pointsPerPlateOther} pts other</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Event Timeline */}
        <div className="egc-section">
          {timelineVisible && (
            <div className="egc-section-content egc-timeline-section-content">
              <div className="egc-timeline">
                <div className="egc-timeline-track">
                  {(() => {
              // Convert primary champion gold events to timeline events (use primaryGoldEvents consistently)
              const primaryEvents = primaryGoldEvents ? [
                ...(primaryGoldEvents.kills || []).map(k => ({ type: 'kill' as const, wave: k.wave, timing: k.timing, gold: k.value, champion: 'primary' as const, cspm: undefined })),
                ...(primaryGoldEvents.turrets || []).map(t => ({ type: 'turret' as const, wave: t.wave, timing: t.timing, gold: t.localGold + t.globalGold, champion: 'primary' as const, cspm: undefined })),
                ...(primaryGoldEvents.plates || []).map(p => ({ type: 'plate' as const, wave: p.wave, timing: p.timing, gold: p.goldValue, champion: 'primary' as const, cspm: undefined })),
                ...(primaryGoldEvents.miscellaneous || []).map(m => ({ type: 'misc' as const, wave: m.wave, timing: m.timing, gold: m.value, champion: 'primary' as const, cspm: undefined })),
                ...(primaryGoldEvents.cspmRanges || []).map(r => ({ type: 'cspm' as const, wave: r.fromWave, timing: waveToTiming(r.fromWave), gold: 0, champion: 'primary' as const, cspm: r.cspm }))
              ] : [];

              // Convert opponent champion gold events to timeline events (use opponentGoldEvents consistently)
              const opponentEvents = opponentGoldEvents ? [
                ...(opponentGoldEvents.kills || []).map(k => ({ type: 'kill' as const, wave: k.wave, timing: k.timing, gold: k.value, champion: 'opponent' as const, cspm: undefined })),
                ...(opponentGoldEvents.turrets || []).map(t => ({ type: 'turret' as const, wave: t.wave, timing: t.timing, gold: t.localGold + t.globalGold, champion: 'opponent' as const, cspm: undefined })),
                ...(opponentGoldEvents.plates || []).map(p => ({ type: 'plate' as const, wave: p.wave, timing: p.timing, gold: p.goldValue, champion: 'opponent' as const, cspm: undefined })),
                ...(opponentGoldEvents.miscellaneous || []).map(m => ({ type: 'misc' as const, wave: m.wave, timing: m.timing, gold: m.value, champion: 'opponent' as const, cspm: undefined })),
                ...(opponentGoldEvents.cspmRanges || []).map(r => ({ type: 'cspm' as const, wave: r.fromWave, timing: waveToTiming(r.fromWave), gold: 0, champion: 'opponent' as const, cspm: r.cspm }))
              ] : [];

              // Combine all events
              const allEvents = [...primaryEvents, ...opponentEvents];

              // Sort by wave, then by champion (primary first for ties), then by timing
              const sortedEvents = allEvents
                .sort((a, b) => a.wave - b.wave || (a.champion === 'primary' ? -1 : 1) || a.timing.localeCompare(b.timing));

              return (
                <>
                  {sortedEvents.length === 0 ? (
                    <div className="egc-timeline-empty">No events added yet. Add kills, turrets, plates, or other gold events to see them here.</div>
                  ) : (
                    sortedEvents.map((event, i) => {
                      const getEventIcon = () => {
                        switch (event.type) {
                          case 'kill':
                            return <img src={gameWideAssets.damageIconWhite} alt="Kill" className="egc-timeline-icon" />;
                          case 'turret':
                            return <img src={gameWideAssets.turretIcon} alt="Turret" className="egc-timeline-icon" />;
                          case 'plate':
                            return <img src={gameWideAssets.turretPlateIcon} alt="Plate" className="egc-timeline-icon" />;
                          case 'cspm':
                            return <img src={gameWideAssets.minionIcon} alt="CSPM" className="egc-timeline-icon" />;
                          case 'misc':
                            return <div className={`egc-timeline-misc-icon ${event.champion}`}>?</div>;
                          default:
                            return <div className="egc-timeline-marker"></div>;
                        }
                      };

                      const championName = event.champion === 'primary' ? primaryChampionName : opponentChampionName;

                      return (
                        <div key={i} className={`egc-timeline-event egc-timeline-${event.type} ${event.champion}`}>
                          {getEventIcon()}
                          <div className="egc-timeline-info">
                            <span className="egc-timeline-wave">W{event.wave}</span>
                            <span className="egc-timeline-time">{event.timing}</span>
                            <span className="egc-timeline-champion">{championName}</span>
                            {event.type === 'cspm' ? (
                              <span className="egc-timeline-cspm">{event.cspm} CSPM</span>
                            ) : (
                              <span className="egc-timeline-gold">+{event.gold}g</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Visual Gold Difference Timeline */}
        <GoldDifferenceTimeline
          primaryChampionName={primaryChampionName}
          opponentChampionName={opponentChampionName}
          kills={primaryGoldEvents?.kills || []}
          plates={primaryGoldEvents?.plates || []}
          turrets={primaryGoldEvents?.turrets || []}
          miscellaneous={primaryGoldEvents?.miscellaneous || []}
          cspmRanges={primaryGoldEvents?.cspmRanges || []}
          opponentKills={opponentGoldEvents?.kills || []}
          opponentPlates={opponentGoldEvents?.plates || []}
          opponentTurrets={opponentGoldEvents?.turrets || []}
          opponentMiscellaneous={opponentGoldEvents?.miscellaneous || []}
          opponentCspmRanges={opponentGoldEvents?.cspmRanges || []}
          primaryGoldInputs={primaryGoldEvents}
          opponentGoldInputs={opponentGoldEvents}
          primaryCalculatedWaves={[]}
          opponentCalculatedWaves={[]}
          primarySummaries={undefined}
          opponentSummaries={undefined}
          primaryWaveProgression={primaryWaveProgression}
          opponentWaveProgression={opponentWaveProgression}
          primaryQuestCompletionWave={primaryQuestCompletion?.withEventsWave ?? primaryQuestCompletion?.baselineWave}
          opponentQuestCompletionWave={opponentQuestCompletion?.withEventsWave ?? opponentQuestCompletion?.baselineWave}
          primaryQuestImmediateGold={primaryRole ? ROLE_QUEST_CONFIG[primaryRole]?.rewards.immediateGold : undefined}
          opponentQuestImmediateGold={opponentRole ? ROLE_QUEST_CONFIG[opponentRole]?.rewards.immediateGold : undefined}
          cspm={primaryGoldEvents?.cspm || goldEvents.cspm}
          className="egc-visual-timeline"
        />
      </div>
    </div>
  );
};

export default GoldControlsV2;
